import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { UserLabel } from '@/components/ui/user-label'
import { formatDate } from '@/lib/formatters'
import {
  LEAD_ACTION_LABELS,
  LEAD_ACTION_RUN_STATUS_LABELS,
  LeadAction,
  type LeadActionAutomation,
  LeadActionRunStatus,
  useCancelLeadActionAutomation,
} from '@/models/leadActionAutomation'
import { StatusChip } from './StatusChip'
import { TagChip } from './TagSelect'

const PROGRESS_VERBS: Record<LeadAction, string> = {
  [LeadAction.GEICO_ELIGIBILITY]: 'checked',
  [LeadAction.COVERWHALE_ELIGIBILITY]: 'checked',
  [LeadAction.FOLLOW]: 'followed',
  [LeadAction.ADD_TAG]: 'tagged',
  [LeadAction.ASSIGN]: 'assigned',
  [LeadAction.CHANGE_STATUS]: 'moved',
}

interface VerdictCountProps {
  value: number
  label: string
  tagId: string | null | undefined
  onFilterByTag: (tagId: string) => void
}

function VerdictCount({ value, label, tagId, onFilterByTag }: VerdictCountProps) {
  if (!value || !tagId) {
    return (
      <span>
        {value} {label}
      </span>
    )
  }
  return (
    <Button
      variant="link"
      size="xs"
      className="h-auto p-0 text-xs underline"
      onClick={() => onFilterByTag(tagId)}
    >
      {value} {label}
    </Button>
  )
}

interface LeadActionRunItemProps {
  run: LeadActionAutomation
  onFilterByTag: (tagId: string) => void
}

export function LeadActionRunItem({ run, onFilterByTag }: LeadActionRunItemProps) {
  const cancel = useCancelLeadActionAutomation()
  const counts = run.leadCounts
  const processed = counts.total - counts.pending
  const verdictTags = run.verdictTags

  function stop() {
    cancel.mutate(run.id, { onError: () => toast.error('Failed to stop the run') })
  }

  return (
    <li className="flex flex-col gap-0.5 text-sm">
      <div className="flex items-center gap-2">
        <span className="min-w-0 truncate font-medium">{LEAD_ACTION_LABELS[run.action]}</span>
        {run.tags.map((tag) => (
          <TagChip key={tag.id} name={tag.name} className="shrink-0" />
        ))}
        {run.assignee && (
          <UserLabel name={run.assignee.name} src={run.assignee.avatar} className="shrink-0" />
        )}
        {run.targetStatus && <StatusChip status={run.targetStatus} className="shrink-0" />}
        <Badge variant="secondary">{LEAD_ACTION_RUN_STATUS_LABELS[run.status]}</Badge>
        <span className="text-muted-foreground shrink-0 text-xs">{formatDate(run.createdAt)}</span>
        {run.status === LeadActionRunStatus.RUNNING && (
          <Button
            variant="outline"
            size="xs"
            className="ml-auto"
            onClick={stop}
            loading={cancel.isPending ? 'Stopping…' : undefined}
          >
            Stop
          </Button>
        )}
      </div>
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-1.5 text-xs">
        <span>
          {processed} of {counts.total} {PROGRESS_VERBS[run.action]}
        </span>
        {verdictTags && (
          <>
            <span>·</span>
            <VerdictCount
              value={counts.eligible}
              label="eligible"
              tagId={verdictTags.eligible}
              onFilterByTag={onFilterByTag}
            />
            <span>·</span>
            <VerdictCount
              value={counts.notEligible}
              label="not eligible"
              tagId={verdictTags.notEligible}
              onFilterByTag={onFilterByTag}
            />
          </>
        )}
        {counts.skipped > 0 && <span>· {counts.skipped} skipped</span>}
        {counts.failed > 0 && <span>· {counts.failed} failed</span>}
      </div>
    </li>
  )
}
